import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { type Board, type Reservation } from './types';
import { exportBoardToExcel, excelFileName } from './excelExport';

/**
 * Choix du format et du contenu de l'export (maquette 16e).
 *
 * Le pied affiche le nom de fichier calculé : on sait ce qu'on va retrouver dans son dossier de
 * téléchargements avant d'avoir cliqué.
 */
export function ExportModal({ board, reservations, companyName, onClose, onExportPdf }: {
    board: Board;
    reservations: Reservation[];
    companyName: string;
    onClose: () => void;
    onExportPdf: () => void;
}) {
    const [format, setFormat] = useState<'xlsx' | 'pdf'>('xlsx');
    const [withFormulas, setWithFormulas] = useState(true);
    const [includeOrigin, setIncludeOrigin] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const run = async () => {
        if (format === 'pdf') { onExportPdf(); onClose(); return; }
        setBusy(true);
        setError('');
        try {
            await exportBoardToExcel({ board, reservations, companyName, includeOrigin, withFormulas });
            onClose();
        } catch (e: any) {
            console.error('Excel export failed', e);
            setError(e?.message || "L'export a échoué.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open
            onClose={onClose}
            size="md"
            icon={<Download size={17} />}
            title="Exporter le tableau"
            subtitle="Les colonnes affichées seulement — masquer une colonne la retire aussi du fichier."
            footer={
                <>
                    <span className="font-mono text-[10.5px] text-content-muted truncate mr-auto">
                        {format === 'xlsx' ? excelFileName(board) : 'Aperçu avant impression'}
                    </span>
                    <Button variant="secondary" onClick={onClose}>Annuler</Button>
                    <Button onClick={run} disabled={busy}>{busy ? 'Génération…' : 'Exporter'}</Button>
                </>
            }
        >
            <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                    <FormatCard label="Excel (.xlsx)" hint="Totaux recalculables" active={format === 'xlsx'} onClick={() => setFormat('xlsx')} />
                    <FormatCard label="PDF" hint="Pour imprimer" active={format === 'pdf'} onClick={() => setFormat('pdf')} />
                </div>

                {format === 'xlsx' && (
                    <>
                        <Check checked={withFormulas} onChange={setWithFormulas}
                            title="Ligne de totaux avec formules"
                            hint="=SUM() plutôt que des nombres figés : corriger un montant recalcule tout." />
                        <Check checked={includeOrigin} onChange={setIncludeOrigin}
                            title="Colonne d'origine"
                            hint="Indique pour chaque ligne si elle vient d'une plateforme ou d'une saisie." />
                    </>
                )}

                {error && <p className="text-xs font-semibold text-state-danger">{error}</p>}
            </div>
        </Modal>
    );
}

function FormatCard({ label, hint, active, onClick }: { label: string; hint: string; active: boolean; onClick: () => void }) {
    return (
        <button type="button" onClick={onClick}
            className={`px-3 py-2.5 rounded-[10px] border text-left transition-colors ${
                active ? 'border-brand-500 bg-brand-500/5' : 'border-border bg-surface-soft hover:border-content'
            }`}>
            <div className="text-[12.5px] font-bold text-content">{label}</div>
            <div className="text-[11px] text-content-muted">{hint}</div>
        </button>
    );
}

function Check({ checked, onChange, title, hint }: { checked: boolean; onChange: (v: boolean) => void; title: string; hint: string }) {
    return (
        <label className="flex items-start gap-2.5 p-3 rounded-[10px] bg-surface-soft border border-border-soft cursor-pointer">
            <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-border accent-brand-600 shrink-0" />
            <span className="text-[12.5px] leading-snug">
                <span className="font-bold block text-content">{title}</span>
                <span className="text-[11px] text-content-muted">{hint}</span>
            </span>
        </label>
    );
}
